import { Package, CheckCircle, XCircle, Coins } from "lucide-react";
import type { OrganizationOrderStats } from "../types";
import { useLocale } from "../../../context/LanguageContext";

interface OrdersStatsBarProps {
  stats?: OrganizationOrderStats;
  isLoading: boolean;
}

function StatCard({
  label,
  value,
  icon: Icon,
  colorClass,
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
      <div className={`rounded-md p-2 ${colorClass}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold text-foreground">{value}</p>
      </div>
    </div>
  );
}

export function OrdersStatsBar({ stats, isLoading }: OrdersStatsBarProps) {
  const { t } = useLocale();

  if (isLoading && !stats) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[72px] animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  const { pendingCount = 0, fulfilledCount = 0, cancelledCount = 0, totalKoinsSpent = 0 } =
    stats ?? {};

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label={t("orders.statsBarPending")}
        value={pendingCount}
        icon={Package}
        colorClass="bg-warning/10 text-warning"
      />
      <StatCard
        label={t("orders.statsBarFulfilled")}
        value={fulfilledCount}
        icon={CheckCircle}
        colorClass="bg-success/10 text-success"
      />
      <StatCard
        label={t("orders.statsBarCancelled")}
        value={cancelledCount}
        icon={XCircle}
        colorClass="bg-destructive/10 text-destructive"
      />
      <StatCard
        label={t("orders.statsBarTotalSpent")}
        value={totalKoinsSpent.toLocaleString()}
        icon={Coins}
        colorClass="bg-koins/10 text-koins"
      />
    </div>
  );
}
